import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled, { useTheme } from "styled-components";
import { FiAlertTriangle, FiArrowDownRight, FiArrowUpRight, FiChevronRight, FiDownload, FiInfo, FiSearch, FiX } from "react-icons/fi";
import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import {
  Chip, Empty, Group, SearchField, Section, SectionHeader, Skeleton, Widget, WidgetGrid, tokens,
} from "../../activity-logs/styles";
import { SegmentedControl } from "../../activity-logs/components/primitives";
import { CreditsPage, Notice, Tag } from "../../creditos/styles";
import { BackButton, ScrolledTopBar } from "../../creditos/components/parts";
import { Capsule, RoundButton } from "../../creditos/components/ControlBar";
import { downloadXml } from "../../creditos/xmlDocs";
import { tokens as T, hairline } from "../../../styles/designTokens";
import { bandOf } from "./analiseData";
import { CoverageHero, Delta, MISS, RING_COLORS, authHeaders, detailsCache } from "./AnaliseDashboard";
import { buildDocuments, buildFornecedor, buildHistory, cfopScope } from "./fornecedorData";
import { branchLabel, formatCnpj, formatCount, formatMonthKey, formatPercent, periodLabel } from "./revisorData";

/**
 * Dashboard de um fornecedor em uma análise do Revisor SPED.
 * Pergunta da tela: este fornecedor está em dia, quanto pesa na análise, está
 * melhorando e onde estão os documentos dele?
 * Elemento próprio: o peso na análise, com todos os fornecedores com faltantes
 * enfileirados e este aceso. Regras dos dados em `fornecedorData.js`.
 */

const API_URL = process.env.REACT_APP_API || "https://api.vidal-app.com";
const STEP = 36;
const NEUTRAL = "rgba(142,142,147,0.55)";
const sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

// Lê /details de uma análise, com o cache da tela de análise e espera em 429
async function loadDetails(id, isCancelled) {
  if (detailsCache.has(id)) return detailsCache.get(id);
  for (let attempt = 0; attempt < 3; attempt += 1) {
    // eslint-disable-next-line no-await-in-loop
    const res = await fetch(`${API_URL}/revisor/reports/${id}/details`, { headers: authHeaders() });
    if (res.status === 429) {
      if (isCancelled()) return null;
      // eslint-disable-next-line no-await-in-loop
      await sleep(4000 * 2 ** attempt);
    } else {
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      // eslint-disable-next-line no-await-in-loop
      const data = await res.json();
      if (!data.success) throw new Error("Resposta sem sucesso");
      const d = { suppliers: data.data.suppliers || [], companies: data.data.companies || [] };
      detailsCache.set(id, d);
      return d;
    }
  }
  throw new Error("Limite de requisições");
}

// ─── Estilos próprios ────────────────────────────────────────────────────────

const Rows = styled.div`
  display: grid;
  grid-template-columns: ${({ $cols }) => ($cols === 2 ? "repeat(2, minmax(0, 1fr))" : "minmax(0, 1fr)")};
  gap: 2px ${({ $cols }) => ($cols === 2 ? "20px" : "0")};
  margin: 12px -6px 0;
  @media (max-width: 720px) { grid-template-columns: minmax(0, 1fr); }

  button {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    column-gap: 12px;
    row-gap: 5px;
    width: 100%;
    padding: 6px;
    border: none;
    border-radius: 9px;
    background: transparent;
    color: inherit;
    font: inherit;
    text-align: left;
    cursor: pointer;
    transition: background-color ${T.durBase} ${T.easeOut};
  }
  button:hover { background: rgba(118,118,128,0.08); }
  button:focus-visible { outline: 2px solid ${T.brand}; outline-offset: -2px; }
  .l { min-width: 0; font-size: 0.84em; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .l small { margin-left: 6px; font-size: 0.88em; font-weight: 400; color: ${({ theme }) => tokens.label3(theme)}; }
  .n { font-size: 0.84em; font-weight: 600; font-variant-numeric: tabular-nums; }
  .track { grid-column: 1 / -1; display: flex; height: 4px; border-radius: 2px; overflow: hidden; background: rgba(118,118,128,0.1); }
  .track span { height: 100%; transition: width 700ms ${T.easeOut}; }
`;

// Colunas finas, como o Tempo de Uso do iOS
const Months = styled.div`
  display: flex;
  align-items: stretch;
  gap: 6px;
  height: 128px;
  margin: 14px -4px 0;

  button {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    gap: 6px;
    padding: 4px;
    border: none;
    border-radius: 10px;
    background: transparent;
    color: inherit;
    font: inherit;
    cursor: pointer;
    transition: background-color ${T.durBase} ${T.easeOut};
  }
  button:hover { background: rgba(118,118,128,0.08); }
  button:focus-visible { outline: 2px solid ${T.brand}; outline-offset: -2px; }
  .n { font-size: 0.76em; font-weight: 600; font-variant-numeric: tabular-nums; }
  .col { width: 100%; max-width: 22px; min-height: 3px; border-radius: 4px 4px 2px 2px; transition: height 700ms ${T.easeOut}; }
  .l { font-size: 0.68em; color: ${({ theme }) => tokens.label2(theme)}; white-space: nowrap; }
`;

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 8px;
  font-size: 0.74em;
  color: ${({ theme }) => tokens.label2(theme)};
  span { display: inline-flex; align-items: center; gap: 6px; }
  i { width: 7px; height: 7px; border-radius: 2px; }
`;

const Big = styled.div`
  margin-top: 12px;
  font-size: 2em;
  font-weight: 700;
  letter-spacing: -0.04em;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  small { margin-left: 6px; font-size: 0.42em; font-weight: 500; letter-spacing: 0; color: ${({ theme }) => tokens.label2(theme)}; }
`;

const Facts = styled.div`
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  div { display: flex; justify-content: space-between; gap: 10px; padding: 8px 0; font-size: 0.82em; border-top: 0.5px solid ${({ theme }) => hairline(theme, true)}; }
  span { color: ${({ theme }) => tokens.label2(theme)}; }
  b { font-weight: 600; font-variant-numeric: tabular-nums; }
`;

const Siblings = styled.div`
  margin: 12px -6px 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  column-gap: 16px;

  button {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 6px;
    border: none;
    border-radius: 10px;
    background: transparent;
    color: inherit;
    font: inherit;
    text-align: left;
    cursor: pointer;
    transition: background-color ${T.durBase} ${T.easeOut};
  }
  button:hover { background: rgba(118,118,128,0.08); }
  button:focus-visible { outline: 2px solid ${T.brand}; outline-offset: -2px; }
  .t { flex: 1; min-width: 0; }
  .t b { display: block; font-size: 0.84em; font-weight: 500; }
  .t span { font-size: 0.74em; color: ${({ theme }) => tokens.label2(theme)}; font-variant-numeric: tabular-nums; }
  .m { font-size: 0.84em; font-weight: 600; font-variant-numeric: tabular-nums; display: inline-flex; align-items: center; gap: 6px; }
  .m i { width: 6px; height: 6px; border-radius: 50%; background: ${MISS}; }
  svg.chev { flex-shrink: 0; font-size: 14px; opacity: 0; color: ${({ theme }) => tokens.label3(theme)}; transition: opacity ${T.durBase} ${T.easeOut}; }
  button:hover svg.chev, button:focus-visible svg.chev { opacity: 1; }
`;

const DocRows = styled(Group)`
  margin-top: 14px;
  padding: 4px 6px 6px;
  font-variant-numeric: tabular-nums;

  .row {
    position: relative;
    display: grid;
    grid-template-columns: minmax(0, 1fr) 96px 104px 30px;
    align-items: center;
    column-gap: 14px;
    padding: 9px 10px;
    border-radius: 12px;
    transition: background-color ${T.durBase} ${T.easeOut};
  }
  .row + .row::before {
    content: "";
    position: absolute;
    top: 0;
    left: 10px;
    right: 10px;
    height: 1px;
    background: ${({ theme }) => hairline(theme)};
    opacity: 0.7;
  }
  .row:hover { background: rgba(118,118,128,0.08); }
  .row:hover::before, .row:hover + .row::before { opacity: 0; }
  .txt { min-width: 0; }
  .name { font-size: 0.92em; font-weight: 500; letter-spacing: -0.01em; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .sub { margin-top: 2px; font-size: 0.78em; color: ${({ theme }) => tokens.label2(theme)}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .date { text-align: right; font-size: 0.8em; color: ${({ theme }) => tokens.label2(theme)}; white-space: nowrap; }
  .status { display: flex; justify-content: flex-end; }
  .dl { opacity: 0.55; transition: opacity ${T.durBase} ${T.easeOut}; }
  .row:hover .dl, .dl:focus-visible { opacity: 1; }
  @media (max-width: 720px) {
    .row { grid-template-columns: minmax(0, 1fr) auto 30px; }
    .date { display: none; }
  }
`;

// ─── Peso na análise ─────────────────────────────────────────────────────────

function WeightStrip({ pending, code }) {
  const theme = useTheme();
  // Raiz quadrada: mantém visíveis os fornecedores com poucos faltantes
  const scale = (v) => Math.max(5, Math.sqrt(v / Math.max(1, pending[0]?.faltantes || 0)) * 100);
  const n = Math.max(1, pending.length);
  const idx = pending.findIndex((p) => p.code === code);
  return (
    <div style={{ position: "relative", marginTop: 14, height: 56 }} aria-hidden="true">
      <svg width="100%" height="100%" viewBox={`0 0 ${n} 100`} preserveAspectRatio="none" style={{ display: "block" }}>
        {pending.map((p, i) => {
          const h = scale(p.faltantes);
          return <rect key={p.code} x={i + 0.12} y={100 - h} width={0.76} height={h} fill={tokens.label3(theme)} opacity={0.38} />;
        })}
      </svg>
      {idx >= 0 && (
        <span style={{
          position: "absolute", bottom: 0, left: `${((idx + 0.5) / n) * 100}%`, width: 3, marginLeft: -1.5, borderRadius: 2, background: MISS,
          height: `${scale(pending[idx].faltantes)}%`,
        }}
        />
      )}
    </div>
  );
}

// ─── Evolução ────────────────────────────────────────────────────────────────

function HistoryTip({ active, payload }) {
  const theme = useTheme();
  const p = active && payload?.[0]?.payload;
  if (!p) return null;
  return (
    <div style={{
      background: tokens.solid(theme), border: `1px solid ${tokens.separator(theme)}`, borderRadius: 10, padding: "8px 11px",
      fontSize: 12, fontVariantNumeric: "tabular-nums", boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
    }}
    >
      <div style={{ fontWeight: 600 }}>{formatMonthKey(p.key, true)}{p.current ? " · esta análise" : ""}</div>
      <div style={{ marginTop: 3, color: tokens.label2(theme) }}>
        {p.present ? `${formatPercent(p.pct)} com XML · ${formatCount(p.encontradas)} com XML · ${formatCount(p.faltantes)} faltantes` : "Sem documentos nesta análise"}
      </div>
    </div>
  );
}

function HistoryChart({ data }) {
  const theme = useTheme();
  return (
    <div style={{ marginTop: 12, flex: 1, minHeight: 124 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 4, right: 0, left: 0, bottom: 0 }} barCategoryGap="38%">
          <XAxis dataKey="key" tickFormatter={(k) => formatMonthKey(k)} tick={{ fontSize: 10, fill: tokens.label2(theme) }} axisLine={false} tickLine={false} interval={0} />
          <YAxis hide />
          <Tooltip content={<HistoryTip />} cursor={{ fill: "rgba(118,118,128,0.08)", radius: 6 }} />
          <Bar dataKey="encontradas" stackId="a" maxBarSize={32} isAnimationActive={false}>
            {data.map((d) => <Cell key={d.key} fill={T.brand} fillOpacity={d.current ? 0.9 : 0.35} />)}
          </Bar>
          <Bar dataKey="faltantes" stackId="a" maxBarSize={32} radius={[4, 4, 0, 0]} isAnimationActive={false}>
            {data.map((d) => <Cell key={d.key} fill={MISS} fillOpacity={d.current ? 0.95 : 0.42} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function historyHeadline(history) {
  const cur = history.findIndex((h) => h.current);
  if (cur < 0) return "";
  const now = history[cur];
  const prev = [...history.slice(0, cur)].reverse().find((h) => h.present);
  if (!prev) return "Primeira análise com documentos deste fornecedor.";
  const when = formatMonthKey(prev.key);
  if (now.faltantes === prev.faltantes) return `Mesmos ${formatCount(now.faltantes)} faltantes de ${when}.`;
  const verb = now.faltantes < prev.faltantes ? "caíram" : "subiram";
  return `Faltantes ${verb} de ${formatCount(prev.faltantes)} para ${formatCount(now.faltantes)} desde ${when}.`;
}

// ─── Documentos ──────────────────────────────────────────────────────────────

const DocRow = React.memo(function DocRow({ d }) {
  const date = d.date ? d.date.toLocaleDateString("pt-BR") : formatMonthKey(d.month);
  return (
    <div className="row">
      <div className="txt">
        <div className="name" title={d.chave}>{d.tipo} {d.numero || "sem número"}</div>
        <div className="sub">
          {d.estabelecimento ? branchLabel(d.estabelecimento) : "Sem estabelecimento"}{d.cfop ? ` · CFOP ${d.cfop}` : ""}
        </div>
      </div>
      <div className="date">{date}</div>
      <div className="status">
        {d.status === "conferido" ? <Tag $color={T.brand}>Conferido</Tag> : <Tag $color="#8e8e93">Não conferido</Tag>}
      </div>
      <RoundButton className="dl" type="button" onClick={() => downloadXml(d.chave)} disabled={!d.chave} aria-label={`Baixar XML de ${d.tipo} ${d.numero}`} title="Baixar XML" style={{ width: 30, height: 30 }}>
        <FiDownload />
      </RoundButton>
    </div>
  );
});

// ─── Tela ────────────────────────────────────────────────────────────────────

export default function FornecedorDashboard({ supplier, report, reports = [], onBack, onOpenSupplier }) {
  const theme = useTheme();
  const reportId = report.id || report._id;

  const [details, setDetails] = useState(() => detailsCache.get(reportId) || null);
  const [xmls, setXmls] = useState(null);
  const [xmlsFailed, setXmlsFailed] = useState(false);
  const [historyById, setHistoryById] = useState({});
  const [historyDone, setHistoryDone] = useState(false);
  const [historyFailed, setHistoryFailed] = useState(0);

  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [focus, setFocus] = useState(null);
  const [visible, setVisible] = useState(STEP);
  const listRef = useRef(null);
  const endRef = useRef(null);

  const loadDocs = useCallback(async () => {
    setXmls(null);
    setXmlsFailed(false);
    try {
      const res = await fetch(`${API_URL}/revisor/reports/${reportId}/suppliers/${supplier.code}/details`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setXmls(data.data?.xmls || { encontradas: [], faltantes: [], sobras: [] });
    } catch (err) {
      console.error("[Revisor] documentos do fornecedor:", err);
      setXmlsFailed(true);
    }
  }, [reportId, supplier.code]);

  useEffect(() => { loadDocs(); }, [loadDocs]);

  // Uma análise por vez, com pausa, para respeitar o limite de requisições
  useEffect(() => {
    let cancelled = false;
    const isCancelled = () => cancelled;
    (async () => {
      const ids = [reportId, ...reports.map((r) => r.id || r._id).filter((id) => id !== reportId)];
      let failures = 0;
      for (let i = 0; i < ids.length && !cancelled; i += 1) {
        const id = ids[i];
        const cached = detailsCache.has(id);
        try {
          // eslint-disable-next-line no-await-in-loop
          const d = await loadDetails(id, isCancelled);
          if (cancelled || !d) return;
          if (id === reportId) setDetails(d);
          setHistoryById((prev) => ({ ...prev, [id]: d }));
        } catch (err) {
          console.error("[Revisor] histórico do fornecedor:", err);
          failures += 1;
        }
        // eslint-disable-next-line no-await-in-loop
        if (!cached && i < ids.length - 1) await sleep(150);
      }
      if (!cancelled) { setHistoryFailed(failures); setHistoryDone(true); }
    })();
    return () => { cancelled = true; };
  }, [reportId, reports]);

  const f = useMemo(() => buildFornecedor(supplier, report, details), [supplier, report, details]);
  const docs = useMemo(() => buildDocuments(xmls), [xmls]);
  const history = useMemo(() => buildHistory(supplier, report, reports, historyById), [supplier, report, reports, historyById]);
  const presentCount = history.filter((h) => h.present).length;
  const loadingDocs = !xmls && !xmlsFailed;

  const applyFocus = (next) => {
    setFocus((prev) => (prev && next && prev.kind === next.kind && prev.value === next.value ? null : next));
    setVisible(STEP);
    if (next) listRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const list = useMemo(() => {
    const term = q.trim().toLowerCase();
    const digits = term.replace(/\D/g, "");
    return docs.docs.filter((d) => {
      if (status !== "all" && d.status !== status) return false;
      if (focus?.kind === "est" && d.estabelecimento !== focus.value) return false;
      if (focus?.kind === "cfop" && d.cfop !== focus.value) return false;
      if (focus?.kind === "month" && d.month !== focus.value) return false;
      if (!term) return true;
      return (digits && (d.numero.includes(digits) || d.chave.includes(digits) || d.cfop.includes(digits)))
        || branchLabel(d.estabelecimento).toLowerCase().includes(term);
    });
  }, [docs, q, status, focus]);

  const hasMore = visible < list.length;
  useEffect(() => {
    if (!hasMore || !endRef.current) return undefined;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible((v) => v + STEP); }, { rootMargin: "800px" });
    io.observe(endRef.current);
    return () => io.disconnect();
  }, [hasMore, list.length, visible]);

  const color = RING_COLORS[bandOf(f.pct).key];
  const deltaTone = f.deltaToReport === null || Math.abs(f.deltaToReport) < 0.05 ? "flat" : f.deltaToReport > 0 ? "up" : "down";
  const listsMatch = docs.conferidos === f.encontradas && docs.sobras === f.faltantes;
  const maxEst = Math.max(1, docs.byEst[0]?.total || 0);
  const maxCfop = Math.max(1, docs.byCfop[0]?.total || 0);
  const maxMonth = Math.max(1, ...docs.byMonth.map((m) => m.total));
  const inPeriod = docs.byMonth.filter((m) => m.key >= f.rep.startKey && m.key <= f.rep.endKey).reduce((s, m) => s + m.total, 0);
  const showMonths = docs.byMonth.length > 1;
  const showSiblings = f.siblings.length > 0;
  const lowerSpan = showMonths && showSiblings ? 2 : 4;

  return (
    <CreditsPage>
      <ScrolledTopBar scrollToTop>
        <div className="title-row">
          <div style={{ minWidth: 0 }}>
            <BackButton onClick={onBack}>{periodLabel(f.rep)}</BackButton>
            <h1 title={supplier.name}>{f.name}</h1>
            <div className="meta">
              <span className="update-tag">{f.cnpj || "Sem CNPJ"}</span>
              {f.own && <span className="update-tag">Estabelecimento do próprio grupo</span>}
            </div>
          </div>
        </div>
      </ScrolledTopBar>

      {/* Resumo e evolução */}
      <Section $first>
        <WidgetGrid>
          <CoverageHero
            pct={f.pct}
            color={color}
            found={f.encontradas}
            missing={f.faltantes}
            stats={[
              { label: "Com XML", value: f.encontradas, dot: T.brand },
              { label: "Faltantes", value: f.faltantes, dot: MISS },
              { label: "Documentos", value: f.total },
            ]}
            note={`${formatCount(f.encontradas)} de ${formatCount(f.total)} documentos deste fornecedor têm XML no projeto. ${f.faltantes > 0
              ? `Faltam ${formatCount(f.faltantes)} XMLs${f.missingShare > 0 ? `, ${formatPercent(f.missingShare)} de todos os faltantes da análise` : ""}.`
              : "Nenhum documento sem XML neste período."}`}
            chips={[
              f.deltaToReport !== null && (
                <Delta key="delta" $tone={deltaTone}>
                  {deltaTone === "up" && <FiArrowUpRight />}
                  {deltaTone === "down" && <FiArrowDownRight />}
                  {f.deltaToReport > 0 ? "+" : ""}{f.deltaToReport.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} p.p.
                  <span>vs. a análise</span>
                </Delta>
              ),
            ].filter(Boolean)}
          />

          <Widget $span={2} $delay={60} style={{ display: "flex", flexDirection: "column" }}>
            <div className="w-title">Evolução entre análises</div>
            <div className="w-sub">{historyDone ? (historyHeadline(history) || "Sem histórico.") : "Carregando as análises do projeto"}</div>
            {presentCount > 0 ? <HistoryChart data={history} /> : !historyDone && <div style={{ marginTop: 12, flex: 1 }}><Skeleton $h={124} $r={12} /></div>}
            {presentCount > 0 && (
              <Legend>
                <span><i style={{ background: T.brand }} />Com XML</span>
                <span><i style={{ background: MISS }} />Faltantes</span>
                {historyFailed > 0 && <span>{formatCount(historyFailed)} {historyFailed === 1 ? "análise não carregou" : "análises não carregaram"}</span>}
              </Legend>
            )}
          </Widget>
        </WidgetGrid>
      </Section>

      {/* Peso e cortes */}
      <Section $delay={80}>
        <WidgetGrid>
          <Widget $delay={100}>
            <div className="w-title">Peso na análise</div>
            {f.rank ? (
              <>
                <Big>{formatCount(f.rank)}º<small>de {formatCount(f.pending.length)} com faltantes</small></Big>
                <WeightStrip pending={f.pending} code={supplier.code} />
                <Facts>
                  <div><span>Dos faltantes</span><b>{formatPercent(f.missingShare)}</b></div>
                  {f.valueShare !== null && <div><span>Do valor dos documentos</span><b>{formatPercent(f.valueShare)}</b></div>}
                </Facts>
              </>
            ) : (
              <>
                <Big style={{ color: T.brand }}>Em dia</Big>
                <div className="w-sub" style={{ marginTop: 8 }}>
                  {details ? `Um dos ${formatCount(f.clean)} fornecedores sem faltantes nesta análise.` : "Sem faltantes nesta análise."}
                </div>
                {f.valueShare !== null && <Facts><div><span>Do valor dos documentos</span><b>{formatPercent(f.valueShare)}</b></div></Facts>}
              </>
            )}
          </Widget>

          {(loadingDocs || docs.byCfop.length > 0) && (
            <Widget $delay={140}>
              <div className="w-title">Operações</div>
              <div className="w-sub">CFOP dos documentos listados</div>
              {loadingDocs ? <div style={{ marginTop: 12 }}><Skeleton $h={120} $r={12} /></div> : (
                <Rows>
                  {docs.byCfop.slice(0, 6).map((c) => (
                    <button key={c.key} type="button" title={cfopScope(c.key)} onClick={() => applyFocus({ kind: "cfop", value: c.key, label: `CFOP ${c.key}` })}>
                      <span className="l">{c.key}<small>{cfopScope(c.key)}</small></span>
                      <span className="n">{formatCount(c.total)}</span>
                      <span className="track"><span style={{ width: `${(c.total / maxCfop) * 100}%`, background: T.brand, opacity: 0.8 }} /></span>
                    </button>
                  ))}
                </Rows>
              )}
            </Widget>
          )}

          {(loadingDocs || docs.byEst.length > 0) && (
            <Widget $span={docs.byCfop.length > 0 || loadingDocs ? 2 : 3} $delay={180}>
              <div className="w-title">Documentos por estabelecimento</div>
              <div className="w-sub">Quem do grupo recebeu, pela lista da API. Clique para filtrar os documentos</div>
              {loadingDocs ? <div style={{ marginTop: 12 }}><Skeleton $h={120} $r={12} /></div> : (
                <>
                  <Rows $cols={docs.byEst.length > 4 ? 2 : 1}>
                    {docs.byEst.slice(0, 12).map((e) => (
                      <button key={e.key} type="button" title={formatCnpj(e.key)} onClick={() => applyFocus({ kind: "est", value: e.key, label: branchLabel(e.key) })}>
                        <span className="l">{branchLabel(e.key)}<small>{formatCnpj(e.key).slice(11)}</small></span>
                        <span className="n">{formatCount(e.total)}</span>
                        <span className="track">
                          <span style={{ width: `${(e.conferidos / maxEst) * 100}%`, background: T.brand, opacity: 0.85 }} />
                          <span style={{ width: `${(e.sobras / maxEst) * 100}%`, background: NEUTRAL }} />
                        </span>
                      </button>
                    ))}
                  </Rows>
                  <Legend>
                    <span><i style={{ background: T.brand }} />Conferidos {formatCount(docs.conferidos)}</span>
                    <span><i style={{ background: NEUTRAL }} />Não conferidos {formatCount(docs.sobras)}</span>
                  </Legend>
                </>
              )}
            </Widget>
          )}
        </WidgetGrid>
      </Section>

      {(showSiblings || (showMonths && !loadingDocs)) && (
        <Section $delay={120}>
          <WidgetGrid>
            {showSiblings && (
              <Widget $span={lowerSpan} $delay={200}>
                <div className="w-title">Mesmo grupo</div>
                <div className="w-sub">
                  {formatCount(f.siblings.length + 1)} CNPJs com a mesma raiz{f.groupMissing > 0 ? ` somam ${formatCount(f.groupMissing)} faltantes` : ", todos sem faltantes"}
                </div>
                <Siblings>
                  {f.siblings.slice(0, 6).map((s) => (
                    <button key={s.code} type="button" onClick={() => onOpenSupplier(s.raw)} title={s.cnpj}>
                      <span className="t">
                        <b>{branchLabel(s.cnpj)}</b>
                        <span>{formatPercent(s.pct)} com XML</span>
                      </span>
                      {s.faltantes > 0 && <span className="m"><i />{formatCount(s.faltantes)}</span>}
                      <FiChevronRight className="chev" />
                    </button>
                  ))}
                </Siblings>
                {f.siblings.length > 6 && <div className="w-foot" style={{ marginTop: 8 }}>E mais {formatCount(f.siblings.length - 6)} na lista de fornecedores da análise.</div>}
              </Widget>
            )}
            {showMonths && !loadingDocs && (
              <Widget $span={lowerSpan} $delay={240}>
                <div className="w-title">Mês de emissão</div>
                <div className="w-sub">
                  {formatCount(inPeriod)} dos {formatCount(docs.docs.length)} documentos listados são do período da análise. Clique para filtrar
                </div>
                <Months>
                  {docs.byMonth.map((m) => {
                    const inside = m.key >= f.rep.startKey && m.key <= f.rep.endKey;
                    return (
                      <button key={m.key} type="button" title={`${formatCount(m.total)} documentos emitidos em ${formatMonthKey(m.key, true)}`} onClick={() => applyFocus({ kind: "month", value: m.key, label: `Emissão em ${formatMonthKey(m.key)}` })}>
                        <span className="n">{formatCount(m.total)}</span>
                        <span className="col" style={{ height: `${Math.max(3, (m.total / maxMonth) * 72)}px`, background: inside ? T.brand : NEUTRAL, opacity: inside ? 0.9 : 1 }} />
                        <span className="l">{formatMonthKey(m.key)}</span>
                      </button>
                    );
                  })}
                </Months>
              </Widget>
            )}
          </WidgetGrid>
        </Section>
      )}

      {/* Documentos */}
      <Section $delay={160} ref={listRef}>
        <SectionHeader><h2>Documentos</h2></SectionHeader>
        {!loadingDocs && !xmlsFailed && !listsMatch && (
          <Notice style={{ marginTop: 0, marginBottom: 12 }}>
            <FiInfo />
            <span>{f.faltantes > 0 ? `A API ainda não lista os ${formatCount(f.faltantes)} faltantes. ` : ""}Estas listas não fecham com o resumo.</span>
          </Notice>
        )}
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <SearchField $max="320px">
            <FiSearch />
            <input placeholder="Número, chave ou CFOP" aria-label="Buscar documento" value={q} onChange={(e) => { setQ(e.target.value); setVisible(STEP); }} />
            {q && <button className="clear" aria-label="Limpar busca" onClick={() => setQ("")}><FiX /></button>}
          </SearchField>
          {focus && <Chip $active onClick={() => applyFocus(null)} aria-label={`Remover filtro ${focus.label}`}>{focus.label} <FiX /></Chip>}
          <div style={{ flex: 1 }} />
          <SegmentedControl
            small
            value={status}
            onChange={(v) => { setStatus(v); setVisible(STEP); }}
            options={[
              { key: "all", label: "Todos", count: formatCount(docs.docs.length) },
              { key: "conferido", label: "Conferidos", count: formatCount(docs.conferidos) },
              { key: "sobra", label: "Não conferidos", count: formatCount(docs.sobras) },
            ]}
          />
        </div>

        {loadingDocs && <div style={{ display: "grid", gap: 8, marginTop: 14 }}>{[0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={i} $h={54} $r={12} />)}</div>}
        {xmlsFailed && (
          <Group style={{ marginTop: 14 }}>
            <Empty>
              <div className="e-icon"><FiAlertTriangle /></div>
              <div className="e-title">Não foi possível carregar os documentos</div>
              <div className="e-sub" style={{ marginBottom: 14 }}>Verifique a conexão e tente de novo.</div>
              <Capsule type="button" onClick={loadDocs} style={{ margin: "0 auto" }}>Tentar de novo</Capsule>
            </Empty>
          </Group>
        )}
        {!loadingDocs && !xmlsFailed && list.length === 0 && (
          <Group style={{ marginTop: 14 }}>
            <Empty>
              <div className="e-icon"><FiSearch /></div>
              <div className="e-title">Nenhum documento</div>
              <div className="e-sub">{q || focus || status !== "all" ? "Nada encontrado com estes filtros." : "A API não listou documentos deste fornecedor."}</div>
            </Empty>
          </Group>
        )}
        {list.length > 0 && <DocRows>{list.slice(0, visible).map((d) => <DocRow key={`${d.status}-${d.chave}`} d={d} />)}</DocRows>}
        {hasMore && (
          <div ref={endRef} style={{ padding: "18px 0", textAlign: "center", fontSize: "0.78em", color: tokens.label2(theme) }}>
            {formatCount(visible)} de {formatCount(list.length)}
          </div>
        )}
      </Section>
    </CreditsPage>
  );
}
